import { Customer, PaginatedResponse, PaginationParams, Order } from '../types';
import { apiClient } from './api';

type PagePayload<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type CartItemRow = {
  productId: number | string;
  name: string;
  slug?: string;
  sku?: string;
  price: number;
  discountPrice?: number | null;
  qty: number;
  stock?: number;
  image?: string;
  lineTotal?: number;
  updatedAt?: string;
  color?: string;
  variant?: string;
};

export type CustomerCart = {
  userId?: number;
  items: CartItemRow[];
  itemCount: number;
  totalQty: number;
  updatedAt?: string | null;
  customer?: {
    id: string;
    name: string;
    phone: string;
    email: string;
  };
};

export type CartSummaryRow = {
  id: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  itemCount: number;
  totalQty: number;
  updatedAt: string;
};

export type CartProductAgg = {
  productId: string;
  name: string;
  slug: string;
  sku: string;
  price: number;
  discountPrice?: number | null;
  stock: number;
  totalQty: number;
  cartCount: number;
  image?: string;
};

function unwrapPage<T>(res: { success: boolean; data?: PagePayload<T>; message?: string; error?: string }): PaginatedResponse<T> {
  if (!res.success || !res.data) {
    throw new Error(res.message || res.error || 'خطا در دریافت داده');
  }
  return {
    data: res.data.data ?? [],
    total: res.data.total ?? 0,
    page: res.data.page ?? 1,
    limit: res.data.limit ?? 10,
    totalPages: res.data.totalPages ?? 1,
  };
}

function unwrapData<T>(res: { success: boolean; data?: T; message?: string; error?: string }, msg: string): T {
  if (!res.success || res.data === undefined) throw new Error(res.message || res.error || msg);
  return res.data;
}

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

function num(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback = ''): string {
  return v == null ? fallback : String(v);
}

/** Normalize cart payloads so admin UI stays stable across API field variants. */
function normalizeCart(raw: unknown): CustomerCart {
  const c = asRecord(raw);
  const itemsRaw = Array.isArray(c.items) ? c.items : [];
  const items: CartItemRow[] = itemsRaw.map((row) => {
    const it = asRecord(row);
    return {
      productId: (it.productId ?? it.product_id ?? '') as string | number,
      name: str(it.name),
      slug: str(it.slug) || undefined,
      sku: str(it.sku) || undefined,
      price: num(it.price),
      discountPrice:
        it.discountPrice === undefined && it.discount_price === undefined
          ? null
          : (it.discountPrice as number | null) ?? (it.discount_price as number | null) ?? null,
      qty: num(it.qty ?? it.quantity, 0),
      stock: num(it.stock, 0),
      image: str(it.image) || undefined,
      lineTotal: it.lineTotal != null ? num(it.lineTotal) : undefined,
      updatedAt: str(it.updatedAt ?? it.updated_at) || undefined,
      color: str(it.color) || undefined,
      variant: str(it.variant) || undefined,
    };
  });
  const totalQty =
    c.totalQty != null || c.total_qty != null
      ? num(c.totalQty ?? c.total_qty)
      : items.reduce((s, i) => s + i.qty, 0);
  const itemCount =
    c.itemCount != null || c.item_count != null ? num(c.itemCount ?? c.item_count) : items.length;
  const customerRaw = c.customer ? asRecord(c.customer) : null;
  return {
    userId: c.userId != null ? num(c.userId) : undefined,
    items,
    itemCount,
    totalQty,
    updatedAt: (c.updatedAt as string | null | undefined) ?? (c.updated_at as string | null | undefined) ?? null,
    customer: customerRaw
      ? {
          id: str(customerRaw.id),
          name: str(customerRaw.name),
          phone: str(customerRaw.phone),
          email: str(customerRaw.email),
        }
      : undefined,
  };
}

function normalizeCartSummary(row: unknown): CartSummaryRow {
  const c = asRecord(row);
  return {
    id: str(c.id),
    customerId: str(c.customerId ?? c.customer_id),
    customerName: str(c.customerName ?? c.customer_name, '—'),
    customerPhone: str(c.customerPhone ?? c.customer_phone),
    itemCount: num(c.itemCount ?? c.item_count),
    totalQty: num(c.totalQty ?? c.total_qty),
    updatedAt: str(c.updatedAt ?? c.updated_at),
  };
}

function normalizeCartProduct(row: unknown): CartProductAgg {
  const p = asRecord(row);
  return {
    productId: str(p.productId ?? p.product_id),
    name: str(p.name),
    slug: str(p.slug),
    sku: str(p.sku),
    price: num(p.price),
    discountPrice:
      p.discountPrice === undefined && p.discount_price === undefined
        ? null
        : (p.discountPrice as number | null) ?? (p.discount_price as number | null) ?? null,
    stock: num(p.stock),
    totalQty: num(p.totalQty ?? p.total_qty),
    cartCount: num(p.cartCount ?? p.cart_count),
    image: str(p.image) || undefined,
  };
}

export const customerService = {
  async getCustomers(params: PaginationParams & { q?: string }): Promise<PaginatedResponse<Customer>> {
    const res = await apiClient.get<PagePayload<Customer>>('/admin/customers', {
      params: { page: params.page, limit: params.limit, q: params.q || undefined },
    });
    return unwrapPage(res);
  },

  async getCustomer(id: string): Promise<Customer> {
    const res = await apiClient.get<Customer>(`/admin/customers/${id}`);
    return unwrapData(res, 'مشتری یافت نشد');
  },

  async updateCustomer(id: string, data: Partial<Customer> & { firstName?: string; lastName?: string }): Promise<Customer> {
    const res = await apiClient.put<Customer>(`/admin/customers/${id}`, data);
    return unwrapData(res, 'به‌روزرسانی ناموفق بود');
  },

  async getCustomerOrders(customerId: string): Promise<Order[]> {
    const res = await apiClient.get<Order[]>(`/admin/customers/${customerId}/orders`);
    if (!res.success) return [];
    return (res.data as Order[]) ?? [];
  },

  async getCustomerCart(customerId: string): Promise<CustomerCart> {
    const res = await apiClient.get<unknown>(`/admin/customers/${customerId}/cart`);
    return normalizeCart(unwrapData(res, 'سبد خرید یافت نشد'));
  },
};

export const cartAdminService = {
  async listCarts(params: PaginationParams): Promise<PaginatedResponse<CartSummaryRow>> {
    const res = await apiClient.get<PagePayload<unknown>>('/admin/carts', {
      params: { page: params.page, limit: params.limit },
    });
    const page = unwrapPage(res);
    return {
      ...page,
      data: (page.data ?? []).map(normalizeCartSummary),
    };
  },

  async listProducts(): Promise<CartProductAgg[]> {
    const res = await apiClient.get<unknown[]>('/admin/carts/products');
    const data = unwrapData(res, 'خطا در دریافت آمار سبدها');
    return (Array.isArray(data) ? data : []).map(normalizeCartProduct);
  },
};
